import React, {useContext, useEffect} from 'react'
import Phaser from 'phaser'
import Modal from '../modal/modal'
import EventLog from '../event-log/event-log'
import Score from '../score/score'
import Config from './config'
import Chance from 'chance'
import Effects from '../effects/effects'
import LoadSplash from '../load-splash/load-splash'
import Nebula from '../nebula/nebula'
import BlitterStarPattern from '../blitter-star-pattern/blitter-star-pattern'
import GameOverOverlay from '../game-over/overlay'
import {saveGame} from './store'
import {enemyMove} from '../opponent/opponent'
import {chooseScene, generateEntityPreviews} from './setup'
import {gameOverCheck} from '../game-over/utils'
import {
    AppContext,
    forceAppUpdate,
    handleBoardClick,
    _addToEventLog,
    _setGameLoading,
    _setGameLoadingPercent,
    _setGameOver,
    _setIsSelectingShips,
    _setMinimapOpen,
    _setModalOpenState,
    _setSelectedShipIds,
    _togglePaused,
} from '../app/app'
import {bindScenePointerEvents} from './pointer'
import {isDebug} from './utils'
import Minimap from '../minimap/minimap'
import handleOverlap from './overlap'
import audio from './audio'
import {deselectAllShips} from '../ship/utils'
import Toolbar from '../toolbar/toolbar'
import DebugButtons from './debug-buttons'
import {demoPan, getBoardPoints} from './utils'

import './launch.css'

window.onbeforeunload = () => {
    if (!window.location.href.includes('launch')) {
        return
    }

    _togglePaused(false)

    // Allow towables to be towed again after refresh
    Object.keys(Config.towables).forEach((key) => {
        const towable = Config.towables[key]
        towable.isBeingTowed = false
        towable.towShipId = null
    })
    saveGame('onbeforeunload')
}

export const destroyGame = () => {
    saveGame('unmount')
    _setGameLoading(true)

    // http://labs.phaser.io/edit.html?src=src\game%20config\game%20destroy.js
    if (Config.scene.sys) {
        Config.scene.sys.game.destroy(true)
    }

    Config.artifacts = {}
    Config.scene = {}
    Config.entities = {}
    Config.ships = {}
    Config.enemyFactions = []
}

export const initGame = (props = {}) => {
    const {isDemo} = props
    function preload() {
        _setGameLoading(true)
        _setGameOver(false)

        this.load.on('progress', function(value) {
            _setGameLoadingPercent(Math.round((value / 1) * 100))
        })

        this.load.image('artifact', './img/artifact.png')
        this.load.image('artifactRed', './img/artifact-red.png')
        this.load.image('artifactGreen', './img/artifact-green.png')
        this.load.image('artifactWhite', './img/artifact-white.png')
        this.load.image('blitterStar1', './img/blitter-star-1.png')
        this.load.image('blitterStar2', './img/blitter-star-2.png')
        this.load.image('disbandWhite', './img/disband-white.png')
        this.load.image('flagGreen', './img/flag-green.png')
        this.load.image('flagRed', './img/flag-red.png')
        this.load.image('flagBlue', './img/flag-blue.png')
        this.load.image('flagOrange', './img/flag-orange.png')
        this.load.image('hammer', './img/hammer-white.png')
        this.load.image('homeGreen', './img/home-green.png')
        this.load.image('homeRed', './img/home-red.png')
        this.load.image('moveMarker', './img/move-marker.png')
        this.load.image('nebula1', './img/nebula1.png')
        this.load.image('nebula2', './img/nebula2.png')
        this.load.image('nebula3', './img/nebula3.png')
        this.load.image('nebula4', './img/nebula4.png')
        this.load.image('nebula5', './img/nebula5.png')
        this.load.image('nebula6', './img/nebula6.png')
        this.load.image('nebula7', './img/nebula7.png')
        this.load.image('nebula8', './img/nebula8.png')
        this.load.image('nebula9', './img/nebula9.png')
        this.load.image('nebula10', './img/nebula10.png')
        this.load.image('nebula11', './img/nebula11.png')
        this.load.image('nebula12', './img/nebula12.png')
        this.load.image('particleWhite', './img/particle-white.png')
        this.load.image('planetOverlay', './img/planet-overlay.png')
        this.load.image('planetFeature1', './img/planet-feature1.png')
        this.load.image('planetFeature2', './img/planet-feature2.png')
        this.load.image('planetFeature3', './img/planet-feature3.png')
        this.load.image('planetFeature4', './img/planet-feature4.png')
        this.load.image('planetFeature5', './img/planet-feature5.png')
        this.load.image('planetFeature6', './img/planet-feature6.png')
        this.load.image('planetFeature7', './img/planet-feature7.png')
        this.load.image('planetFeature8', './img/planet-feature8.png')
        this.load.image('question', './img/question.png')
        this.load.image('resourcesGreen', './img/resources-green.png')
        this.load.image('resourcesWhite', './img/resources-white.png')
        this.load.image('satelliteGreen', './img/satellite-green.png')
        this.load.image(
            'satelliteGreenRank1',
            './img/satellite-green-rank-1.png'
        )
        this.load.image(
            'satelliteGreenRank2',
            './img/satellite-green-rank-2.png'
        )
        this.load.image(
            'satelliteGreenRank3',
            './img/satellite-green-rank-3.png'
        )
        this.load.image('satelliteRed', './img/satellite-red.png')
        this.load.image('satelliteWhite', './img/satellite-white.png')
        this.load.image('satelliteBlue', './img/satellite-blue.png')
        this.load.image('satelliteOrange', './img/satellite-orange.png')
        this.load.image('score', './img/score.png')
        this.load.image('selectShips', './img/select-ships.png')
        this.load.image('shipGreen', './img/ship-green.png')
        this.load.image('shipGreenRank1', './img/ship-green-rank-1.png')
        this.load.image('shipGreenRank2', './img/ship-green-rank-2.png')
        this.load.image('shipGreenRank3', './img/ship-green-rank-3.png')
        this.load.image('shipRed', './img/ship-red.png')
        this.load.image('shipWhite', './img/ship-white.png')
        this.load.image('shipBlue', './img/ship-blue.png')
        this.load.image('shipOrange', './img/ship-orange.png')
        this.load.image('starEntity', './img/star-entity.png')
        this.load.image('star', './img/star.png')
        this.load.image('undiscovered', './img/undiscovered.png')

        this.load.audio('soundButtonClick', ['./sound/button-click.mp3'])
        this.load.audio('soundConstructionComplete', [
            './sound/construction-complete.mp3',
        ])
        this.load.audio('soundEntitySelect1', ['./sound/entity-select-1.mp3'])
        this.load.audio('soundGameWin', ['./sound/game-win.mp3'])
        this.load.audio('soundGameLose', ['./sound/game-lose.mp3'])

        this.load.audio('soundMoveMarker1', ['./sound/ship-move-marker-1.mp3'])
        this.load.audio('soundPlanetColonize', ['./sound/planet-colonize.mp3'])
        this.load.audio('soundPlanetMine', ['./sound/planet-mine.mp3'])
        this.load.audio('soundResourcesAdd', ['./sound/resources-add.mp3'])
        this.load.audio('soundResourcesSubtract', [
            './sound/resources-subtract.mp3',
        ])
        this.load.audio('soundScore1', ['./sound/score-1.mp3'])
        this.load.audio('soundShipEnterOrbit1', [
            './sound/ship-enter-orbit-1.mp3',
        ])
        this.load.audio('soundShipDisband', ['./sound/ship-disband.mp3'])
        this.load.audio('soundShipExplode1', ['./sound/ship-explode-1.mp3'])
        this.load.audio('soundShipExplode2', ['./sound/ship-explode-2.mp3'])
        this.load.audio('soundShipSensor1', ['./sound/ship-sensor-1.mp3'])
        this.load.audio('soundShipSelect1', ['./sound/ship-select-1.mp3'])
        this.load.audio('soundShipShoot1', ['./sound/ship-shoot-1.mp3'])
        this.load.audio('soundTrack1', ['./sound/track-1.mp3'])
        this.load.audio('soundTrack2', ['./sound/track-2.mp3'])
        this.load.audio('soundTrack3', ['./sound/track-3.mp3'])
        this.load.audio('soundTrack4', ['./sound/track-4.mp3'])
        this.load.audio('soundTractorBeam1', ['./sound/tractor-beam-1.mp3'])

        this.load.plugin(
            'rexpinchplugin',
            './plugins/rexpinchplugin.min.js',
            true
        )
    }

    function create() {
        Config.scene = this
        Config.chance = new Chance()
        Config.isDemo = isDemo

        !isDemo && audio.init(this)

        this.matter.world.update30Hz()
        this.matter.world.engine.world.gravity.x = 0
        this.matter.world.engine.world.gravity.y = 0
        // this.matter.world.engine.world.gravity.scale = 0

        if (isDebug) {
            this.matter.world.debugConfig.showSleeping = true
            // this.matter.world.debugConfig.showCollisions = true
            // this.matter.world.debugConfig.showBroadphase = true
            // this.matter.world.debugConfig.showBounds = true
        }

        const difficulty = Config.difficultyLevels[Config.difficulty]

        // Autosave Timer
        this.time.addEvent({
            delay: Config.autoSaveInterval,
            callback: () => saveGame('autosave'),
            loop: true,
        })

        // Opponent Move Timer
        this.time.addEvent({
            delay: difficulty.enemyMoveInterval,
            callback: enemyMove,
            loop: true,
        })

        // Resource Timer
        this.time.addEvent({
            delay: difficulty.planetMineResourceDelay,
            callback: () => {
                if (Config.isGameOver) {
                    return
                }
                // audio.resources(true)

                Object.keys(Config.entities).forEach((key) => {
                    const entity = Config.entities[key]

                    if (!entity.isMiningResources) {
                        return
                    }

                    Config.gameStats.resources +=
                        difficulty.planetMineResourceRate

                    new Effects().resourceAnim({
                        x: entity.x,
                        y: entity.y,
                        num: difficulty.planetMineResourceRate,
                        positive: true,
                        type: 'resources',
                    })
                })
                forceAppUpdate()
                saveGame('resource timer')
            },
            loop: true,
        })

        // Demo pan timer
        if (Config.isDemo) {
            this.time.addEvent({
                delay: 5000,
                callback: demoPan,
                loop: true,
            })
        }

        // Reset sensor warning sound
        Config.soundTriggers = {}
        Config.soundTriggers.shipSensorWarnSound = false
        Config.scene.time.addEvent({
            delay: 15000,
            callback: () => {
                Config.soundTriggers.shipSensorWarnSound = false
            },
            loop: true,
        })

        // Play atmospheric sounds
        audio.atmospheric()
        Config.scene.time.addEvent({
            delay: 15000,
            callback: () => {
                audio.atmospheric()
            },
        })

        //
        // Config.scene.time.addEvent({
        //     delay: 2000,
        //     callback: () => {
        //         // console.log('scene children', Config.scene.children.list)
        //         console.log('children length', Config.scene.children.length)
        //
        //         const ignore = [
        //             'boardGraphics boundary',
        //             'flag',
        //             'star blitter',
        //             'nebula',
        //             'entity label',
        //             'entity statusLabel',
        //             'orbitGraphics',
        //             'homeSprite',
        //             'resourcesSprite',
        //             'gradientArc',
        //             'planet feature',
        //             'planet overlay sprite',
        //             'selectionGraphics',
        //             'ship label',
        //             'ship',
        //             'ship select box',
        //             'healthBarRect',
        //             'pointAnim symbol',
        //             'pointAnim text',
        //             'pointAnim image',
        //         ]
        //         const filtered = Config.scene.children.list.filter(
        //             (child) =>
        //                 ignore.indexOf(child[Config.namespace]?.type) === -1
        //         )
        //         // console.log(filtered)
        //
        //         // const unknownChildren = Config.scene.children.list.filter(
        //         //     (child) => !child[Config.namespace]
        //         // )
        //         // console.log('unknownChildren', unknownChildren)
        //     },
        //     loop: true,
        // })

        // http://brm.io/matter-js/docs/classes/Grid.html
        Config.scene.matter.grid.create({
            bucketHeight: 200, // Default 48
            bucketWidth: 200, // Default 48
        })

        // Wait until all planets have been placed and we have the board size
        chooseScene({isDemo})
            .then((props) => {
                isDebug && console.log('Config', Config)

                Config.scene.sound.setMute(Config.isMuted)

                const camera = this.cameras.main
                const boardSize = Config.boardSizes[Config.boardSize]

                // camera.roundPixels = true
                camera.setBounds(
                    -Config.boardPadding,
                    -Config.boardPadding,
                    boardSize.boardWidth + Config.boardPadding * 2,
                    boardSize.boardHeight + Config.boardPadding * 2
                )

                // camera.setViewport(
                //     0,
                //     0,
                //     window.screen.width * 3,
                //     window.screen.height * 3
                // )

                // camera.setZoom(2) // Phaser.Math.Clamp(camera.zoom, 0.5, 1))

                _setGameLoading(false)

                if (isDemo || isDebug) {
                    if (isDemo) {
                        const position = getBoardPoints(boardSize)
                        camera.zoomTo(2, 0)
                        camera.centerOn(position.center.x, position.center.y)

                        camera.pan(
                            position.topCenter.x,
                            position.topCenter.y + 300,
                            10000,
                            'Linear',
                            true
                        )
                        camera.zoomTo(
                            Config.zoomDefault,
                            Config.zoomDefaultTime
                        )
                    }
                } else {
                    gameOverCheck('launch')

                    if (props?.planet) {
                        const {planet} = props

                        camera.centerOn(planet.x, planet.y)
                        camera.zoomTo(
                            Config.zoomDefault,
                            Config.zoomDefaultTime
                        )
                        saveGame('create')

                        Config.minimap = new Minimap()

                        if (!Config.isGameOver) {
                            _addToEventLog()
                            _addToEventLog({
                                html: `Starting on the planet <span style="color: #${Config.colorGreen}">${planet.name}</span>`,
                                coords: {x: planet.x, y: planet.y},
                            })
                        }
                    }
                }

                // Matter boundaries
                Config.scene.matter.world.setBounds(
                    0,
                    0,
                    boardSize.boardWidth,
                    boardSize.boardHeight
                )

                // Board boundary
                const boardRect = new Phaser.Geom.Rectangle(
                    0,
                    0,
                    boardSize.boardWidth,
                    boardSize.boardHeight
                )
                const boardGraphics = Config.scene.add.graphics({
                    lineStyle: {width: 2, color: 0xffffff, alpha: 0.1},
                })
                boardGraphics.strokeRectShape(boardRect)
                boardGraphics.setDepth(1)
                boardGraphics[Config.namespace] = {
                    type: 'boardGraphics boundary',
                }

                // Generate background stars
                const {blitterStarCount} = Config.boardSizes[Config.boardSize]
                const parallaxStarsWidth = boardSize.boardWidth * 2
                const parallaxStarsHeight = boardSize.boardHeight * 2

                new BlitterStarPattern({
                    alpha: 0.35,
                    count: blitterStarCount / 2,
                    texture: 'blitterStar1',
                })

                new BlitterStarPattern({
                    alpha: 0.45,
                    count: blitterStarCount / 2,
                    texture: 'blitterStar2',
                })

                const parallaxStars1 = new BlitterStarPattern({
                    alpha: 0.35,
                    count: blitterStarCount,
                    height: parallaxStarsHeight,
                    texture: 'blitterStar1',
                    width: parallaxStarsWidth,
                    x: -parallaxStarsWidth / 4,
                    y: -parallaxStarsHeight / 4,
                })

                const parallaxStars2 = new BlitterStarPattern({
                    alpha: 0.5,
                    count: blitterStarCount,
                    height: parallaxStarsHeight,
                    texture: 'blitterStar2',
                    width: parallaxStarsWidth,
                    x: -parallaxStarsWidth / 4,
                    y: -parallaxStarsHeight / 4,
                })

                bindScenePointerEvents(Config, parallaxStars1, parallaxStars2)

                // Generate nebulas
                for (let i = 0; i < 12; i += 1) {
                    new Nebula({startOnScreen: i <= 6})
                }

                // Get orientation: Phaser.Scale.PORTRAIT, Phaser.Scale.LANDSCAPE
                let orientation = null
                Config.scene.scale.on('orientationchange', (o) => {
                    orientation = o
                })

                // On resize
                Config.scene.game.scale.on(
                    'resize',
                    (
                        gameSize,
                        baseSize,
                        displaySize,
                        previousWidth,
                        previousHeight
                    ) => {
                        if (
                            gameSize.width === window.innerWidth &&
                            gameSize.height === window.innerHeight
                        ) {
                            return
                        }

                        isDebug && console.log('Resize')

                        // Fit game screen to viewport
                        // https://photonstorm.github.io/phaser3-docs/Phaser.Scale.ScaleManager.html
                        // https://rexrainbow.github.io/phaser3-rex-notes/docs/site/scalemanager/
                        Config.scene.game.scale.resize(
                            window.innerWidth,
                            window.innerHeight
                        )

                        // Attempt to stay in the same area after orientation change
                        if (orientation) {
                            const {
                                x: cameraX,
                                y: cameraY,
                            } = Config.scene.cameras.main.worldView
                            Config.scene.cameras.main.centerOn(
                                cameraX + previousWidth / 2.5,
                                cameraY + previousHeight / 2.5
                            )
                            orientation = null
                        }

                        // Reset mini/menus
                        Config.minimap && Config.minimap.hide()
                        Config.minimap = new Minimap()
                        _setMinimapOpen(false)
                        _setIsSelectingShips(false)
                        _setModalOpenState(false)
                        deselectAllShips()
                        forceAppUpdate()
                    }
                )
            })
            .then(() => {
                if (!isDemo) {
                    generateEntityPreviews(Config.entities)
                }
            })
    }

    function update(time, delta) {
        for (const key in Config.entities) {
            Config.entities[key].update(time, delta)
        }

        for (const key in Config.ships) {
            Config.ships[key].update(time, delta)
        }

        for (const key in Config.towables) {
            Config.towables[key].update(time, delta)
        }

        for (const key in Config.timers) {
            Config.timers[key].update(time, delta)
        }

        handleOverlap()

        // Synchronize selected ships react state with game state
        // Probably a bad idea but performance doesn't seem to be impacted
        _setSelectedShipIds(Config.selectedShips)
    }

    new Phaser.Game({
        // type: Phaser.CANVAS, // Phaser.WEBGL,
        width: '100%',
        height: '100%',
        backgroundColor: '#000',
        transparent: true,
        parent: 'game',
        pixelArt: false,
        // https://github.com/photonstorm/phaser/blob/master/CHANGELOG-v3.50.md#removal-of-resolution-property-from-across-the-api
        // zoom: 1,
        // TODO: While this does change game size correctly on resize,
        // it does not work on orientation change
        // scale: {
        //     parent: 'game',
        //     mode: Phaser.Scale.RESIZE,
        //     autoCenter: Phaser.Scale.CENTER_BOTH,
        // },
        // autoRound: true,
        clearBeforeRender: false,
        scene: {
            preload,
            create,
            update,
        },
        // fps: {
        //     target: 30,
        //     min: 30,
        // },
        // https://photonstorm.github.io/phaser3-docs/Phaser.Types.Physics.Matter.html
        physics: {
            default: 'matter',
            matter: {
                debug: isDebug,
                debugShowBody: isDebug,
                debugShowVelocity: isDebug,
                debugShowStaticBody: isDebug,
                debugShowSleeping: isDebug,
                debugWireframes: isDebug,
                constraintIterations: 1, // Default 2
                positionIterations: 1, // Default 6
                velocityIterations: 1, // Default 4
                enableSleeping: true,
                // sleepThreshold: 1, // Default 60
            },
        },
    })
}

const Launch = () => {
    const {isGameLoading, isPaused, togglePaused} = useContext(AppContext)

    useEffect(() => {
        initGame()

        return function destroy() {
            destroyGame()
        }
    }, [])

    return (
        <div className="launch">
            {isPaused && (
                <div
                    className="launch-paused-overlay"
                    onClick={() => {
                        togglePaused(false)
                        Config.scene.game.scene.resume('default')
                    }}
                >
                    Paused
                </div>
            )}
            <DebugButtons />
            <GameOverOverlay />
            <LoadSplash hide={!isGameLoading} />
            <Modal />
            <Score />
            <EventLog />

            <Toolbar />
            <div id="game" className="game" onPointerUp={handleBoardClick} />
            <div className="launch-bg" />
            <div id={Config.entityPreviewHolderId} className="display-none" />
        </div>
    )
}

export default Launch
